import apiCall from "auth/ApiCallInstance";
import { urlBase64ToUint8Array } from "../utils/pushClient";
import { API_BASE_URL } from "configs/AppConfig";

const API = API_BASE_URL;

function waitForController(timeoutMs = 2500) {
  if (navigator.serviceWorker.controller) return Promise.resolve();
  return new Promise((resolve) => {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      navigator.serviceWorker.removeEventListener("controllerchange", onChange);
      resolve();
    };
    const onChange = () => finish();
    const timer = setTimeout(finish, timeoutMs);
    navigator.serviceWorker.addEventListener("controllerchange", onChange);
  });
}

/**
 * Register this browser for admin web-push and save the subscription server-side.
 * @returns {Promise<{ ok: true }>}
 */
export async function enablePushForAdmin() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    throw new Error("Push notifications are not supported in this browser");
  }

  const reg = await navigator.serviceWorker.register("/sw.js", { scope: "/" });
  await navigator.serviceWorker.ready;
  // Never block forever — subscribe works on the registration without a controller.
  await waitForController(2500);

  const perm = await Notification.requestPermission();
  if (perm !== "granted") {
    throw new Error("Notification permission was not granted");
  }

  const { data } = await apiCall.get(`${API}/push/public-key`);
  const publicKey = data?.publicKey;
  if (!publicKey) {
    throw new Error("Server has no VAPID public key configured");
  }

  let sub = await reg.pushManager.getSubscription();

  // If VAPID keys were rotated, drop the old browser subscription and recreate.
  if (sub) {
    try {
      const existingKey = sub.options?.applicationServerKey;
      const expected = urlBase64ToUint8Array(publicKey);
      const sameKey =
        existingKey &&
        existingKey.byteLength === expected.byteLength &&
        new Uint8Array(existingKey).every((b, i) => b === expected[i]);
      if (!sameKey) {
        await sub.unsubscribe();
        sub = null;
      }
    } catch (_) {
      try {
        await sub.unsubscribe();
      } catch (__) {}
      sub = null;
    }
  }

  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const plainSub = sub.toJSON ? sub.toJSON() : JSON.parse(JSON.stringify(sub));
  if (!plainSub?.endpoint || !plainSub?.keys?.p256dh || !plainSub?.keys?.auth) {
    throw new Error("Browser returned an invalid push subscription");
  }

  await apiCall.post(`${API}/push/subscribe`, plainSub, {
    headers: { "Content-Type": "application/json" },
  });

  return { ok: true };
}

/** Drop local browser subscription so the next enable creates a fresh one. */
export async function unsubscribeLocalPush() {
  try {
    const reg =
      (await navigator.serviceWorker.getRegistration("/sw.js")) ||
      (await navigator.serviceWorker.getRegistration());
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) await sub.unsubscribe();
  } catch (_) {}
}
